import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Coins, FileText, Download, X, Lock } from "lucide-react";
import { coachStart, coachSend, listPlans, planUrl, createPlan } from "@/lib/pay";
import { cached, setCached } from "@/lib/me";
import { SubNav } from "@/components/revanche/SubNav";
import { CreditSheet } from "@/components/revanche/CreditSheet";
import { ReferralPopup } from "@/components/revanche/ReferralPopup";
import { OfferSheet } from "@/components/revanche/OfferSheet";
import { BottomSheet } from "@/components/revanche/BottomSheet";
import { canShowReferral } from "@/lib/referral";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch (e) { return ""; } };

const Coach = () => {
    const navigate = useNavigate();
    const c0 = cached("coach");
    const [cid, setCid] = useState(c0?.conversation_id || null);
    const [messages, setMessages] = useState(c0?.messages || []);
    const [themes, setThemes] = useState(c0?.themes || []);
    const [credits, setCredits] = useState(c0?.credits ?? 0);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [gate, setGate] = useState(false);
    const [creditOpen, setCreditOpen] = useState(false);
    const [plansOpen, setPlansOpen] = useState(false);
    const [plans, setPlans] = useState([]);
    const [refOpen, setRefOpen] = useState(false);
    const [offerOpen, setOfferOpen] = useState(false);
    const pending = useRef(null);
    const endRef = useRef(null);

    useEffect(() => {
        coachStart()
            .then((d) => { setCid(d.conversation_id); setMessages(d.messages || []); setThemes(d.themes || []); setCredits(d.credits ?? 0); setCached("coach", d); })
            .catch((e) => { if (e.response?.status === 402) setGate(true); });
    }, []);

    useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

    const refreshPlans = () => listPlans().then(setPlans).catch(() => {});

    const generatePlan = async () => {
        setMessages((m) => [...m, { role: "assistant", content: "Je te prépare ton plan, deux minutes…", pending: true }]);
        setBusy(true);
        try {
            const d = await createPlan();
            setMessages((m) => [...m.filter((x) => !x.pending), { role: "plan", plan: d }]);
            setCredits(d.credits ?? credits);
            refreshPlans();
            if (d.plans_count === 3 && canShowReferral()) setTimeout(() => setRefOpen(true), 800);
        } catch (e) {
            setMessages((m) => m.filter((x) => !x.pending));
            if (e.response?.status === 402) { pending.current = null; setCreditOpen(true); }
            else setMessages((m) => [...m, { role: "assistant", content: "Petit souci technique pour le plan, redemande-le moi." }]);
        } finally { setBusy(false); }
    };

    const send = async (t) => {
        const content = (t ?? text).trim();
        if (!content || busy || !cid) return;
        setText("");
        setMessages((m) => [...m, { role: "user", content }]);
        setBusy(true);
        try {
            const d = await coachSend(cid, content);
            setMessages((m) => [...m, { role: "assistant", content: d.content }]);
            if (d.followup) setMessages((m) => [...m, { role: "assistant", content: d.followup }]);
            setCredits(d.credits ?? credits);
            if (d.credits_exhausted) { pending.current = content; setCreditOpen(true); }
            if (d.plan_pending) { setBusy(false); await generatePlan(); return; }
        } catch (e) {
            setMessages((m) => [...m, { role: "assistant", content: "Petit souci technique, réessaie." }]);
        } finally { setBusy(false); }
    };

    const onCreditsPaid = async () => {
        setCreditOpen(false);
        try { const d = await coachStart(); setCredits(d.credits ?? credits); } catch (e) {}
        const resume = pending.current;
        pending.current = null;
        if (resume) setTimeout(() => send(resume), 400);
    };

    const openPlans = () => { refreshPlans(); setPlansOpen(true); };

    if (gate) {
        return (
            <div className="fixed inset-0 bg-black max-w-[480px] mx-auto flex flex-col" data-testid="coach-locked">
                <div className="shrink-0 safe-top px-5 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid var(--border)" }}>
                    <span className="font-display font-black text-white text-xl">Mike</span>
                    <Lock size={18} className="text-revtext-secondary" />
                </div>
                <button onClick={() => setOfferOpen(true)} data-testid="coach-locked-body"
                    className="flex-1 min-h-0 w-full flex flex-col items-center justify-center gap-4 px-6 text-center">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ border: "1px solid var(--border)", background: "var(--surface)" }}>
                        <Lock size={26} className="text-revred" />
                    </div>
                    <h2 className="font-display font-black uppercase text-white text-2xl">Le coach Mike est verrouillé</h2>
                    <p className="font-body" style={{ fontSize: 15, color: "var(--text-secondary)", maxWidth: 300 }}>
                        Débloque ton analyse pour discuter avec Mike et recevoir tes plans.
                    </p>
                    <span className="font-display font-black uppercase text-white rounded-full" style={{ height: 52, lineHeight: "52px", padding: "0 28px", background: "var(--red)" }}>
                        Débloquer
                    </span>
                </button>
                <SubNav />
                <OfferSheet open={offerOpen} onClose={() => setOfferOpen(false)} onPaid={() => { setOfferOpen(false); setGate(false); coachStart().then((d) => { setCid(d.conversation_id); setMessages(d.messages || []); setThemes(d.themes || []); setCredits(d.credits ?? 0); }).catch(() => {}); }} />
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black max-w-[480px] mx-auto flex flex-col" data-testid="coach-screen">
            <div className="shrink-0 safe-top px-5 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid var(--border)" }}>
                <div className="flex items-center gap-4">
                    <span className="font-display font-black text-white text-xl">Mike</span>
                    <button data-testid="btn-my-plans" onClick={openPlans}
                        className="tap-feedback font-body" style={{ fontSize: 13, color: "var(--text-secondary)", textDecoration: "underline" }}>
                        Mes plans
                    </button>
                </div>
                <div className="flex items-center gap-1.5" data-testid="coach-credits">
                    <Coins size={18} className="text-revred" strokeWidth={1.8} />
                    <span className="font-display font-black text-white" style={{ fontSize: 18 }}>{credits}</span>
                </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-none px-4 py-4 flex flex-col gap-3">
                {messages.map((m, i) => {
                    if (m.role === "plan") {
                        return (
                            <div key={i} data-testid={`plan-card-${i}`} className="self-start rounded-2xl p-4 max-w-[85%]" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                                <div className="flex items-center gap-2 mb-2"><FileText size={16} className="text-revred" /><span className="font-body font-semibold text-white text-sm">{m.plan.titre}</span></div>
                                <div className="flex gap-2 mt-3">
                                    <a data-testid={`plan-open-${i}`} href={planUrl(m.plan.plan_id)} target="_blank" rel="noreferrer"
                                        className="tap-feedback flex-1 text-center rounded-full text-white font-body" style={{ height: 40, lineHeight: "40px", background: "var(--red)", fontSize: 14 }}>Ouvrir</a>
                                    <a data-testid={`plan-dl-${i}`} href={planUrl(m.plan.plan_id, true)}
                                        className="tap-feedback rounded-full flex items-center justify-center" style={{ width: 44, height: 40, border: "1px solid var(--border)", color: "var(--text-secondary)" }}><Download size={16} /></a>
                                </div>
                            </div>
                        );
                    }
                    return (
                        <div key={i} className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap font-body ${m.role === "user" ? "self-end text-white" : "self-start text-white"}`}
                            style={{ background: m.role === "user" ? "var(--surface-2)" : "var(--surface)" }}>
                            {m.content}
                        </div>
                    );
                })}
                {messages.length <= 1 && themes.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-1">
                        {themes.map((th) => {
                            const P = { "Nutrition": "la nutrition", "Sport": "le sport", "Style": "le style",
                                "Peau": "la peau", "Cheveux et barbe": "les cheveux et la barbe", "Mental": "le mental" };
                            const phrase = P[th] || th.toLowerCase();
                            return (
                                <button key={th} data-testid={`theme-${th}`} onClick={() => send(`On commence par ${phrase}.`)}
                                    className="tap-feedback px-3 py-2 rounded-full font-body text-white text-sm active:border-revred" style={{ border: "1px solid var(--border)" }}>{th}</button>
                            );
                        })}
                    </div>
                )}
                {busy && <div className="self-start px-4 py-2.5 rounded-2xl text-sm font-body" style={{ background: "var(--surface)", color: "var(--text-secondary)" }}>Mike écrit…</div>}
                <div ref={endRef} />
            </div>

            <div className="shrink-0 px-3 pb-3 pt-1 flex items-center gap-2" style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}>
                <input data-testid="coach-input" value={text} onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Écris à Mike…"
                    className="flex-1 bg-black rounded-full px-4 text-white outline-none focus:border-revred" style={{ fontSize: "16px", height: 56, border: "1px solid var(--border)" }} />
                <button data-testid="coach-send" onClick={() => send()} disabled={busy}
                    className="tap-feedback rounded-full bg-revred flex items-center justify-center disabled:opacity-50" style={{ width: 56, height: 56 }}>
                    <Send size={20} className="text-white" />
                </button>
            </div>
            <SubNav />

            <CreditSheet open={creditOpen} onClose={() => setCreditOpen(false)} onPaid={onCreditsPaid} />
            <ReferralPopup open={refOpen} onClose={() => setRefOpen(false)} />

            <BottomSheet open={plansOpen} onClose={() => setPlansOpen(false)} height="auto" testId="plans-sheet">
                <div className="px-6 pt-2 pb-6 relative">
                    <button data-testid="plans-close" onClick={() => setPlansOpen(false)} className="tap-feedback absolute right-4 top-1" style={{ color: "var(--text-secondary)" }}><X size={22} /></button>
                    <h2 className="font-body font-bold text-white mt-4" style={{ fontSize: 22 }}>Mes plans</h2>
                    <div className="mt-4 flex flex-col gap-3 max-h-[50dvh] overflow-y-auto scrollbar-none">
                        {plans.length === 0 && <p className="font-body" style={{ fontSize: 14, color: "var(--text-secondary)" }}>Aucun plan pour l'instant. Demande-en un à Mike.</p>}
                        {plans.map((p) => (
                            <div key={p.plan_id} data-testid={`plans-item-${p.plan_id}`} className="rounded-2xl p-4" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
                                <p className="font-body font-semibold text-white text-sm">{p.titre}</p>
                                <p className="font-body mt-1" style={{ fontSize: 12, color: "var(--text-secondary)" }}>Créé le {fmtDate(p.created_at)}</p>
                                <div className="flex gap-2 mt-3">
                                    <a href={planUrl(p.plan_id)} target="_blank" rel="noreferrer" className="tap-feedback flex-1 text-center rounded-full text-white font-body" style={{ height: 40, lineHeight: "40px", background: "var(--red)", fontSize: 14 }}>Ouvrir</a>
                                    <a href={planUrl(p.plan_id, true)} className="tap-feedback rounded-full flex items-center justify-center" style={{ width: 44, height: 40, border: "1px solid var(--border)", color: "var(--text-secondary)" }}><Download size={16} /></a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </BottomSheet>
        </div>
    );
};

export default Coach;
